import { FC, useCallback, useEffect, useState } from 'react'
import { useSearchParams } from '@/hooks/useSearchParams'
import usePagination from '@/hooks/usePagination'
import Pagination from '@UI/New/Pagination'
import CustomTable from '@common/CustomTable/Table'
import lessonTableStyles from '@pages/Admin/Reports/components/lessonTable.module.css'
import EditEntityModal from '@modals/EditEntityModal/EditEntityModal'
import { reportAPI } from '@/api'
import { IGroupLessonPaymentByLessonItem } from '../../utils/types'
import { DEFAULT_DATES } from '../../utils/constants'
import useTableColumns from './useTableColumns'
import cn from './index.module.css'
import ChangeStudentsLessonStatusModal from './modals/ChangeStudentsLessonStatusModal/ChangeStudentsLessonStatusModal'
import ViewLessonIncomeModal from './modals/ViewLessonIncomeModal/ViewLessonIncomeModal'
import { useSelector } from 'react-redux'
import { createPortal } from 'react-dom'
import LessonStatusCards from '@pages/Admin/AllUsers/modals/EditStudentModal/components/LessonStatusesTab/LessonStatusCards'
import { getStatusDescription } from '../../utils/lessonStatus'

interface IByLessonsTable {
  studentId?: number | string | null
  directionId?: string | null
  startDate?: string | null
  endDate?: string | null
  status?: string | null
  refreshKey?: string
  onReportChange?: () => void
  viewMode?: 'table' | 'cards'
  paginationContainer?: HTMLElement | null
}

const MOCK_DATA: IGroupLessonPaymentByLessonItem[] = [
  {
    id: 1,
    index: 1,
    date_time: '2026-05-05T15:00:00',
    seminarian: {
      id: 104,
      name: 'Кузнецова Ольга Викторовна',
      avatar_path: 'https://randomuser.me/api/portraits/women/55.jpg',
    } as IUser,
    course: {
      id: 1,
      name: 'Основы богословия',
    } as ICourse,
    group: {
      id: 11,
      name: 'Группа А-101',
    } as IGroup,
    status: 5,
    duration: '90 мин',
    group_student_count: 18,
    visited_count: 16,
    missed_count: 2,
    income: 24000,
    salary: 9000,
    comment: 'Занятие проведено по плану',
  },
  {
    id: 2,
    index: 2,
    date_time: '2026-05-07T18:30:00',
    seminarian: {
      id: 105,
      name: 'Смирнов Алексей Павлович',
      avatar_path: 'https://randomuser.me/api/portraits/men/32.jpg',
    } as IUser,
    course: {
      id: 2,
      name: 'История Церкви',
    } as ICourse,
    group: {
      id: 12,
      name: 'Группа Б-202',
    } as IGroup,
    status: 10,
    duration: '90 мин',
    group_student_count: 22,
    visited_count: 0,
    missed_count: 22,
    income: 0,
    salary: 0,
    comment: 'Отмена со стороны ученика',
  },
  {
    id: 3,
    index: 3,
    date_time: '2026-05-09T11:00:00',
    seminarian: {
      id: 106,
      name: 'Орлова Марина Сергеевна',
      avatar_path: 'https://randomuser.me/api/portraits/women/44.jpg',
    } as IUser,
    course: {
      id: 3,
      name: 'Литургика',
    } as ICourse,
    group: {
      id: 13,
      name: 'Группа В-303',
    } as IGroup,
    status: 11,
    duration: '60 мин',
    group_student_count: 15,
    visited_count: 0,
    missed_count: 15,
    income: 0,
    salary: 0,
    comment: 'Педагог перенес занятие',
  },
  {
    id: 4,
    index: 4,
    date_time: '2026-05-12T16:00:00',
    seminarian: {
      id: 107,
      name: 'Федоров Илья Николаевич',
      avatar_path: 'https://randomuser.me/api/portraits/men/67.jpg',
    } as IUser,
    course: {
      id: 4,
      name: 'Догматическое богословие',
    } as ICourse,
    group: {
      id: 14,
      name: 'Группа Г-404',
    } as IGroup,
    status: 12,
    duration: '120 мин',
    group_student_count: 19,
    visited_count: 0,
    missed_count: 19,
    income: 12000,
    salary: 3000,
    comment: 'Платная отмена',
  },
]

const SORTING_COLUMNS = [
  'date-time',
  'seminarian',
  'franchise',
  'course',
  'group',
  'status',
  'student-status',
  'duration',
  'group-student-count',
  'visited-count',
  'missed-count',
  'income',
  'salary',
  'profit',
  'comment',
]

const ByLessonsTable: FC<IByLessonsTable> = ({
  studentId,
  directionId: selectedDirectionId,
  startDate,
  endDate,
  status,
  refreshKey,
  onReportChange,
  viewMode = 'table',
  paginationContainer,
}) => {
  const role = useSelector((state: any) => state.profile.profile?.role)
  const teacherCanEdit = useSelector(
    (state: any) => state.settings?.isTeacherCanEditStatuses === '1',
  )
  const canEdit = ['admin', 'junior-admin'].includes(role) || teacherCanEdit
  const [startDateFromSearch] = useSearchParams(
    'start-date',
    DEFAULT_DATES.startDate,
  )
  const [endDateFromSearch] = useSearchParams('end-date', DEFAULT_DATES.endDate)
  const [directionId] = useSearchParams('direction-id')
  const [groupId] = useSearchParams('group-id')
  const [search] = useSearchParams('all-search')
  const [seminarianId] = useSearchParams('all-seminarian-id')
  const [studentIdFromSearch] = useSearchParams('all-student-id')
  const [statusFromSearch] = useSearchParams('all-status')
  const [franchiseIds] = useSearchParams('franchise-ids')
  const effectiveStartDate =
    startDate ?? startDateFromSearch ?? DEFAULT_DATES.startDate
  const effectiveEndDate = endDate ?? endDateFromSearch ?? DEFAULT_DATES.endDate
  const effectiveStatus = status ?? (!studentId ? statusFromSearch : null)
  const effectiveDirectionId =
    selectedDirectionId === undefined ? directionId : selectedDirectionId
  const [userModal, setUserModal] = useState<IUser | null>(null)
  const [lessonStatusModal, setLessonStatusModal] =
    useState<IGroupLessonPaymentByLessonItem | null>(null)
  const [lessonIncomeModal, setLessonIncomeModal] =
    useState<IGroupLessonPaymentByLessonItem | null>(null)
  const [sorting, setSorting] = useState<
    { key: string; order: 'asc' | 'desc' | 'none' }[]
  >(SORTING_COLUMNS.map((item) => ({ key: item, order: 'none' })))

  const onSortingClick = (key: string) => {
    setSorting((prev) =>
      prev.map((item) => {
        const order =
          key === item.key
            ? item.order === 'none'
              ? 'asc'
              : item.order === 'asc'
              ? 'desc'
              : 'none'
            : 'none'

        return { ...item, order }
      }),
    )
  }

  const getFilters = () => {
    const filterParams: Record<string, string> = {}
    const sort = sorting.find((item) => item.order !== 'none')

    if (sort) {
      filterParams['sort_order'] = sort.order
      filterParams['sort_key'] = sort.key
    }

    if (effectiveStartDate) {
      filterParams['start_date'] = effectiveStartDate
    }

    if (effectiveEndDate) {
      filterParams['end_date'] = effectiveEndDate
    }

    if (effectiveDirectionId) {
      filterParams['direction_id'] = effectiveDirectionId
    }

    if (!studentId && groupId) {
      filterParams['group_id'] = groupId
    }

    if (studentId || studentIdFromSearch) {
      filterParams['student_id'] = String(studentId || studentIdFromSearch)
    }
    if (!studentId && seminarianId) filterParams['seminarian_id'] = seminarianId
    if (!studentId) filterParams['status_scope'] = 'lesson'

    if (effectiveStatus && effectiveStatus !== 'all') {
      // В карточке ученика фильтруем личный статус, в общем отчёте — статус занятия.
      filterParams['status'] = String(effectiveStatus)
    }

    if (!studentId && search) {
      filterParams['search'] = search
    }
    if (!studentId && franchiseIds) filterParams['franchise_ids'] = franchiseIds

    return filterParams
  }

  const {
    state,
    loading,
    getState: getData,
    ...params
  } = usePagination({
    api: reportAPI.groupLessonPayment.byLessons,
    params: { ...getFilters() },
    firstRequest: false,
    latestRequestOnly: true,
  })

  useEffect(() => {
    const handler = setTimeout(() => {
      getData({ page: 1 })
      params.setCurrentPage(1)
    }, 250)

    return () => {
      clearTimeout(handler)
    }
  }, [
    effectiveStartDate,
    effectiveEndDate,
    effectiveDirectionId,
    groupId,
    studentId,
    studentIdFromSearch,
    seminarianId,
    effectiveStatus,
    search,
    sorting,
    role,
    franchiseIds,
    refreshKey,
  ])

  const data = state as IGroupLessonPaymentByLessonItem[]

  const onChangeStatusClick = useCallback(
    (lesson: IGroupLessonPaymentByLessonItem) => {
      setLessonStatusModal(lesson)
    },
    [],
  )

  const onIncomeClick = useCallback(
    (lesson: IGroupLessonPaymentByLessonItem) => {
      setLessonIncomeModal(lesson)
    },
    [],
  )

  const columns = useTableColumns({
    onSeminarianClick: (seminarian) => setUserModal(seminarian),
    onChangeStatusClick,
    onIncomeClick,
    showStudentStatus: Boolean(studentId),
  })
  const pagination = <Pagination compact={!!paginationContainer} {...params} />

  return (
    <>
      <div className={cn.content}>
        {viewMode === 'cards' ? (
          <LessonStatusCards
            loading={loading}
            items={data.map((lesson) => {
              const income = lesson.income ?? null
              const salary = lesson.salary ?? null

              return {
                id: lesson.id,
                dateTime: lesson.date_time,
                status: lesson.student_status ?? lesson.status,
                statusText: getStatusDescription(
                  String(lesson.student_status ?? lesson.status),
                ),
                teacher: lesson.seminarian?.name,
                course: lesson.course?.name,
                group: lesson.group?.name,
                duration: lesson.duration,
                studentCount: lesson.group_student_count,
                visitedCount: lesson.visited_count,
                missedCount: lesson.missed_count,
                income,
                salary,
                profit:
                  income !== null && salary !== null ? income - salary : null,
                comment: lesson.comment,
                isPaid: lesson.is_paid,
                hasEnoughBalance: lesson.has_enough_balance,
                onEdit: canEdit
                  ? () => setLessonStatusModal(lesson)
                  : undefined,
              }
            })}
          />
        ) : (
          <CustomTable<IGroupLessonPaymentByLessonItem>
            className={lessonTableStyles.table}
            flexColumns
            compact
            dense={Boolean(studentId)}
            dragScroll={Boolean(studentId)}
            data={data}
            columns={columns}
            loading={loading}
            sorting={sorting}
            onSortingClick={onSortingClick}
          />
        )}
        {paginationContainer
          ? createPortal(pagination, paginationContainer)
          : pagination}
      </div>
      {!!userModal && (
        <EditEntityModal
          type="employee"
          open={!!userModal}
          employeeId={userModal?.id ?? null}
          onDismiss={() => setUserModal(null)}
          onUpdate={() => setUserModal(null)}
        />
      )}
      <ChangeStudentsLessonStatusModal
        open={!!lessonStatusModal}
        lesson={lessonStatusModal}
        onDismiss={() => setLessonStatusModal(null)}
        onSuccess={() => {
          setLessonStatusModal(null)
          getData()
          onReportChange?.()
        }}
      />
      <ViewLessonIncomeModal
        open={!!lessonIncomeModal}
        lesson={lessonIncomeModal}
        onDismiss={() => setLessonIncomeModal(null)}
      />
    </>
  )
}

export default ByLessonsTable
